import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Mail, Phone, MapPin, Send, Building2, MessageCircle, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { COMPANY } from '../lib/company';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({ title: 'Mesaj trimis!', description: 'Te vom contacta în cel mai scurt timp.' });
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="bg-white min-h-screen">
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm flex items-center gap-2 text-neutral-500">
          <Link to="/" className="hover:text-amber-600 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Acasă
          </Link>
          <span>/</span>
          <span className="text-neutral-900">Contact</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs uppercase tracking-[0.25em] text-amber-700 font-medium">Suntem aici pentru tine</span>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-neutral-900 mt-3">Contactează-ne</h1>
          <p className="mt-4 text-neutral-600">
            Echipa AXA îți răspunde rapid la orice întrebare despre produse, comenzi sau livrare.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <div className="space-y-4">
              <a
                href={`tel:${COMPANY.phoneClean}`}
                className="flex items-center gap-4 bg-white border border-neutral-200 rounded-2xl p-5 hover:border-amber-400 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  <Phone className="w-5 h-5 text-amber-700 group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Telefon</div>
                  <div className="font-semibold text-neutral-900 text-lg">{COMPANY.phone}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">Luni - Vineri, 09:00 - 18:00</div>
                </div>
              </a>

              <a
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 bg-white border border-neutral-200 rounded-2xl p-5 hover:border-amber-400 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-amber-700 group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Email</div>
                  <div className="font-semibold text-neutral-900 break-all">{COMPANY.email}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">Răspundem în maxim 24h</div>
                </div>
              </a>

              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white border border-neutral-200 rounded-2xl p-5 hover:border-green-400 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-500 group-hover:text-white transition-colors">
                  <MessageCircle className="w-5 h-5 text-green-700 group-hover:text-white" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">WhatsApp</div>
                  <div className="font-semibold text-neutral-900">{COMPANY.phone}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">Conversație rapidă, online acum</div>
                </div>
              </a>

              <div className="flex items-start gap-4 bg-white border border-neutral-200 rounded-2xl p-5">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Adresă</div>
                  <div className="font-semibold text-neutral-900">{COMPANY.sediu}</div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white border border-neutral-200 rounded-2xl p-5">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-neutral-500">Program</div>
                  <div className="text-sm text-neutral-700 mt-0.5">
                    <div className="flex justify-between"><span>Luni - Vineri</span> <span className="font-medium text-neutral-900">09:00 - 18:00</span></div>
                    <div className="flex justify-between"><span>Sâmbătă</span> <span className="font-medium text-neutral-900">10:00 - 14:00</span></div>
                    <div className="flex justify-between"><span>Duminică</span> <span className="text-neutral-500">Închis</span></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-neutral-50 border border-neutral-200 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-4 h-4 text-neutral-700" />
                <h3 className="font-semibold text-neutral-900 text-sm">Date firmă</h3>
              </div>
              <ul className="text-xs text-neutral-600 space-y-1.5">
                <li><strong className="text-neutral-800">Denumire:</strong> {COMPANY.name}</li>
                <li><strong className="text-neutral-800">CUI:</strong> {COMPANY.cui}</li>
                <li><strong className="text-neutral-800">Reg. Comerțului:</strong> {COMPANY.regCom}</li>
                <li><strong className="text-neutral-800">EUID:</strong> {COMPANY.euid}</li>
              </ul>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 space-y-4 h-fit">
            <h2 className="font-serif font-bold text-2xl text-neutral-900">Trimite un mesaj</h2>
            <p className="text-sm text-neutral-500 -mt-2">Completează formularul și revenim cu un răspuns.</p>
            {[
              { name: 'name', label: 'Nume', type: 'text' },
              { name: 'email', label: 'Email', type: 'email' },
              { name: 'subject', label: 'Subiect', type: 'text' },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-xs font-medium text-neutral-700 mb-1">{f.label} *</label>
                <input
                  required
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm transition-colors"
                />
              </div>
            ))}
            <div>
              <label className="block text-xs font-medium text-neutral-700 mb-1">Mesaj *</label>
              <textarea
                required
                rows="5"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full border border-neutral-300 focus:border-amber-500 outline-none px-4 py-2.5 rounded-lg text-sm transition-colors"
              />
            </div>
            <button type="submit" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold px-6 py-3 rounded-full transition-colors">
              <Send className="w-4 h-4" /> Trimite mesajul
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
